import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { ScrollArea } from '../components/ui/scroll-area';
import { toast } from 'sonner';
import { ArrowLeft, Save, Loader2 } from 'lucide-react';

import BasicInfoSection from '../components/edit/BasicInfoSection';
import EligibilitySection from '../components/edit/EligibilitySection';
import LocationSection from '../components/edit/LocationSection';
import StaffNotesSection from '../components/edit/StaffNotesSection';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const EditResourcePage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { getAuthHeaders } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [neighborhoods, setNeighborhoods] = useState([]);
  
  useEffect(() => {
    const fetchNeighborhoods = async () => {
      try {
        const response = await axios.get(`${API_URL}/public/neighborhoods`);
        setNeighborhoods(response.data.neighborhoods || []);
      } catch (error) {
        console.error('Failed to fetch neighborhoods:', error.message);
        setNeighborhoods(['Roxbury', 'Dorchester', 'Mattapan', 'East Boston', 'Other']);
      }
    };
    fetchNeighborhoods();
  }, []);
  
  const [formData, setFormData] = useState({
    resource_name: '', organization: '', primary_sdoh_category: '',
    secondary_sdoh_categories: [], client_facing_description: '',
    populations_served: [], eligibility_requirements: '', cost_to_client: 'Free',
    service_modality: [], neighborhoods_served: [], zip_codes: [],
    languages_offered: [], physical_address: '', phone_number: '',
    website: '', hours: '', how_to_access: '', referral_required: false,
    warm_handoff_possible: false, known_barriers: '', staff_tips: ''
  });

  useEffect(() => {
    const fetchResource = async () => {
      try {
        const response = await axios.get(`${API_URL}/resources/${id}`, { headers: getAuthHeaders() });
        setFormData(response.data);
      } catch (error) {
        toast.error('Failed to load resource');
        navigate('/');
      } finally {
        setLoading(false);
      }
    };
    fetchResource();
  }, [id, getAuthHeaders, navigate]);

  const handleChange = (field, value) => setFormData(prev => ({ ...prev, [field]: value }));

  const handleMultiSelect = (field, value, checked) => {
    setFormData(prev => ({
      ...prev,
      [field]: checked ? [...prev[field], value] : prev[field].filter(v => v !== value)
    }));
  };

  const handleArrayInput = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value.split(',').map(item => item.trim()).filter(Boolean) }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.resource_name || !formData.organization || !formData.primary_sdoh_category) {
      toast.error('Please fill in all required fields');
      return;
    }
    setSaving(true);
    try {
      await axios.put(`${API_URL}/resources/${id}`, formData, { headers: getAuthHeaders() });
      toast.success('Resource updated successfully!');
      navigate(`/resource/${id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update resource');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAF8F5] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#5C4023]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAF8F5]">
      <header className="sticky top-0 z-40 bg-[#FAF8F5]/95 backdrop-blur border-b border-[#E0D6C8]">
        <div className="max-w-3xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="icon" onClick={() => navigate(`/resource/${id}`)} data-testid="back-btn" className="rounded-full hover:bg-[#F3EFE9]">
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div className="flex items-center gap-2">
                <span className="font-['Manrope'] text-base tracking-tight mr-1"><span className="font-extrabold text-[#5C4023]">617</span><span className="font-medium text-[#C87D3E]">Reach</span></span>
                <h1 className="text-lg font-bold text-[#1A1A1A] font-['Manrope']">Edit Resource</h1>
              </div>
            </div>
            <Button onClick={handleSubmit} disabled={saving} data-testid="save-resource-btn" className="bg-[#5C4023] hover:bg-[#4A331C] rounded-full px-6">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
              Save
            </Button>
          </div>
        </div>
      </header>

      <ScrollArea className="h-[calc(100vh-80px)]">
        <main className="max-w-3xl mx-auto px-4 py-6">
          <form onSubmit={handleSubmit} className="space-y-6">
            <BasicInfoSection formData={formData} onChange={handleChange} onMultiSelect={handleMultiSelect} />
            <EligibilitySection formData={formData} onChange={handleChange} onMultiSelect={handleMultiSelect} />
            <LocationSection formData={formData} neighborhoods={neighborhoods} onChange={handleChange} onMultiSelect={handleMultiSelect} onArrayInput={handleArrayInput} />
            <StaffNotesSection formData={formData} onChange={handleChange} />

            <div className="sticky bottom-4 pt-4 md:hidden">
              <Button type="submit" disabled={saving} data-testid="save-resource-mobile-btn" className="w-full h-14 bg-[#5C4023] hover:bg-[#4A331C] rounded-2xl text-lg font-medium shadow-lg">
                {saving ? <Loader2 className="w-5 h-5 animate-spin mr-2" /> : <Save className="w-5 h-5 mr-2" />}
                Save Changes
              </Button>
            </div>
          </form>
        </main>
      </ScrollArea>
    </div>
  );
};

export default EditResourcePage;
